import { useState, useMemo, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { PromptSection, PromptField, PromptFieldRow, PromptPreview } from '../components/PromptBuilder'
import { Button, Icon } from '../components/common'
import {
  QUALITY_PRESETS,
  ASPECT_RATIOS,
  CAMERA_ANGLES,
  FOCAL_LENGTHS,
  DEPTH_OF_FIELD,
  CAMERA_MOVEMENTS,
  DEFAULT_PROMPT_STATE,
  generatePrompt,
  generateNegativePrompt,
} from '../config/promptPresets'
import { mapJsonToPromptState, getJsonTemplate } from '../config/promptImport'
import './PromptsView.css'

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(4px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }
}

// Section animation variants
const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  })
}

/**
 * PromptsView - Visual prompt builder for crafting image generation prompts
 */
export default function PromptsView() {
  // Main prompt state
  const [promptState, setPromptState] = useState(DEFAULT_PROMPT_STATE)
  
  // Notification state
  const [notification, setNotification] = useState(null) // { type: 'success' | 'error', message: string }
  
  // File input ref
  const fileInputRef = useRef(null)

  // Helper to update section state
  const updateSection = useCallback((sectionKey, updates) => {
    setPromptState(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          ...updates,
        },
      },
    }))
  }, [])

  // Helper to update field values
  const updateValue = useCallback((sectionKey, fieldKey, value) => {
    setPromptState(prev => ({
      ...prev,
      values: {
        ...prev.values,
        [sectionKey]: {
          ...prev.values[sectionKey],
          [fieldKey]: value,
        },
      },
    }))
  }, [])

  // Generate prompts
  const generatedPrompt = useMemo(() => generatePrompt(promptState), [promptState])
  const negativePrompt = useMemo(() => generateNegativePrompt(promptState), [promptState])

  // Reset all fields
  const handleReset = () => {
    if (window.confirm('Reset all fields to default values?')) {
      setPromptState(DEFAULT_PROMPT_STATE)
    }
  }

  // Enable all sections
  const handleEnableAll = () => {
    setPromptState(prev => ({
      ...prev,
      sections: Object.keys(prev.sections).reduce((acc, key) => {
        acc[key] = { ...prev.sections[key], enabled: true, collapsed: false }
        return acc
      }, {}),
    }))
  }

  // Disable all sections
  const handleDisableAll = () => {
    setPromptState(prev => ({
      ...prev,
      sections: Object.keys(prev.sections).reduce((acc, key) => {
        acc[key] = { ...prev.sections[key], enabled: false, collapsed: true }
        return acc
      }, {}),
    }))
  }

  // Show notification helper
  const showNotification = (type, message) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }

  // Handle JSON file import
  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const text = await file.text()
      const json = JSON.parse(text)
      const newState = mapJsonToPromptState(json)
      setPromptState(newState)
      showNotification('success', `Imported: ${json.shot_id || file.name}`)
    } catch (err) {
      console.error('Failed to import JSON:', err)
      showNotification('error', 'Failed to parse JSON file')
    }

    // Reset file input so same file can be selected again
    e.target.value = ''
  }

  // Copy template to clipboard
  const handleCopyTemplate = async () => {
    try {
      await navigator.clipboard.writeText(getJsonTemplate())
      showNotification('success', 'Template copied to clipboard!')
    } catch (err) {
      console.error('Failed to copy template:', err)
      showNotification('error', 'Failed to copy to clipboard')
    }
  }

  const { sections, values } = promptState

  return (
    <motion.div 
      className="prompts-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <header className="prompts-view__header">
        <div className="prompts-view__title-group">
          <span className="prompts-view__icon"><Icon name="prompts" size={28} /></span>
          <h1 className="prompts-view__title">Prompt Builder</h1>
        </div>
        <p className="prompts-view__subtitle">
          Craft image generation prompts with visual controls
        </p>
      </header>

      {/* Quick Actions */}
      <div className="prompts-view__actions">
        <Button variant="primary" size="sm" onClick={handleImportClick}>
              <Icon name="import" size={16} /> Import JSON
        </Button>
        <Button variant="secondary" size="sm" onClick={handleCopyTemplate}>
          <Icon name="copy" size={14} /> Copy Template
        </Button>
        <div className="prompts-view__actions-divider" />
        <Button variant="ghost" size="sm" onClick={handleEnableAll}>
          Enable All
        </Button>
        <Button variant="ghost" size="sm" onClick={handleDisableAll}>
          Disable All
        </Button>
        <Button variant="ghost" size="sm" onClick={handleReset}>
          <Icon name="trash" size={14} /> Reset
        </Button>
      </div>

      {/* Hidden file input for JSON import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Notification Toast */}
      <AnimatePresence>
        {notification && (
          <motion.div
            className={`prompts-view__notification prompts-view__notification--${notification.type}`}
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
            transition={{ duration: 0.2 }}
          >
            <span className="prompts-view__notification-icon">
              {notification.type === 'success' ? <Icon name="check" size={16} /> : <Icon name="x" size={16} />}
            </span>
            <span>{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="prompts-view__layout">
        {/* Sections Column */}
        <div className="prompts-view__sections">
          
          {/* Quality & Style Section */}
          <motion.div
            custom={0}
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
          >
            <PromptSection
              title="Quality & Style"
              iconName="shots"
              enabled={sections.quality.enabled}
              collapsed={sections.quality.collapsed}
              onToggleEnabled={() => updateSection('quality', { enabled: !sections.quality.enabled })}
              onToggleCollapsed={() => updateSection('quality', { collapsed: !sections.quality.collapsed })}
            >
              <PromptFieldRow>
                <PromptField
                  label="Quality Preset"
                  type="select"
                  value={values.quality.preset}
                  onChange={(v) => updateValue('quality', 'preset', v)}
                  options={QUALITY_PRESETS}
                />
                <PromptField
                  label="Aspect Ratio"
                  type="select"
                  value={values.quality.aspectRatio}
                  onChange={(v) => updateValue('quality', 'aspectRatio', v)}
                  options={ASPECT_RATIOS}
                />
              </PromptFieldRow>
              
              <PromptField
                label="Additional Style Notes"
                type="text"
                value={values.quality.styleNote}
                onChange={(v) => updateValue('quality', 'styleNote', v)}
                placeholder="e.g., moody lighting, cinematic color grade"
              />
              
              <PromptField
                label="Include Negative Prompt"
                type="toggle"
                value={values.quality.includeNegative}
                onChange={(v) => updateValue('quality', 'includeNegative', v)}
              />
              
              {values.quality.includeNegative && (
                <PromptField
                  label="Custom Negative Prompt"
                  type="textarea"
                  value={values.quality.negativePrompt}
                  onChange={(v) => updateValue('quality', 'negativePrompt', v)}
                  placeholder="Leave blank to use preset default"
                  hint="Override default negative prompt"
                  rows={2}
                />
              )}
            </PromptSection>
          </motion.div>

          {/* Subject Section */}
          <motion.div
            custom={1}
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
          >
            <PromptSection
              title="Subject"
              iconName="characters"
              enabled={sections.subject.enabled}
              collapsed={sections.subject.collapsed}
              onToggleEnabled={() => updateSection('subject', { enabled: !sections.subject.enabled })}
              onToggleCollapsed={() => updateSection('subject', { collapsed: !sections.subject.collapsed })}
            >
              <PromptField
                label="Description"
                type="textarea"
                value={values.subject.description}
                onChange={(v) => updateValue('subject', 'description', v)}
                placeholder="Describe the main subject of the image..."
                rows={3}
              />
              
              <PromptFieldRow>
                <PromptField
                  label="Attire / Clothing"
                  type="text"
                  value={values.subject.attire}
                  onChange={(v) => updateValue('subject', 'attire', v)}
                  placeholder="e.g., vintage 90s clothes, band t-shirt"
                />
                <PromptField
                  label="Facial Features"
                  type="text"
                  value={values.subject.facialFeatures}
                  onChange={(v) => updateValue('subject', 'facialFeatures', v)}
                  placeholder="e.g., warm smile, tired eyes"
                />
              </PromptFieldRow>
              
              <PromptField
                label="Props"
                type="text"
                value={values.subject.props}
                onChange={(v) => updateValue('subject', 'props', v)}
                placeholder="e.g., relevant props, period items, personal belongings"
              />
              
              <PromptField
                label="Consistency Anchor"
                type="text"
                value={values.subject.consistencyAnchor}
                onChange={(v) => updateValue('subject', 'consistencyAnchor', v)}
                placeholder="Key details to maintain across frames"
                hint="For multi-shot consistency"
              />
            </PromptSection>
          </motion.div>

          {/* Environment Section */}
          <motion.div
            custom={2}
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
          >
            <PromptSection
              title="Environment"
              iconName="locations"
              enabled={sections.environment.enabled}
              collapsed={sections.environment.collapsed}
              onToggleEnabled={() => updateSection('environment', { enabled: !sections.environment.enabled })}
              onToggleCollapsed={() => updateSection('environment', { collapsed: !sections.environment.collapsed })}
            >
              <PromptField
                label="Location"
                type="textarea"
                value={values.environment.location}
                onChange={(v) => updateValue('environment', 'location', v)}
                placeholder="e.g., interior of a small local business, period-appropriate"
                rows={2}
              />
              
              <PromptFieldRow>
                <PromptField
                  label="Global Lighting"
                  type="text"
                  value={values.environment.lightingGlobal}
                  onChange={(v) => updateValue('environment', 'lightingGlobal', v)}
                  placeholder="e.g., warm tungsten + fluorescent mix"
                />
                <PromptField
                  label="Accent Lighting"
                  type="text"
                  value={values.environment.lightingAccent}
                  onChange={(v) => updateValue('environment', 'lightingAccent', v)}
                  placeholder="e.g., neon sign glow, CRT glow"
                />
              </PromptFieldRow>
              
              <PromptField
                label="Background Details"
                type="textarea"
                value={values.environment.backgroundDetails}
                onChange={(v) => updateValue('environment', 'backgroundDetails', v)}
                placeholder="e.g., wall decor, period-appropriate furniture, front counter"
                rows={2}
              />
              
              <PromptField
                label="Atmosphere / Mood"
                type="text"
                value={values.environment.atmosphere}
                onChange={(v) => updateValue('environment', 'atmosphere', v)}
                placeholder="e.g., nostalgic, slightly eerie, cozy"
              />
              
              <PromptField
                label="Consistency Anchor"
                type="text"
                value={values.environment.consistencyAnchor}
                onChange={(v) => updateValue('environment', 'consistencyAnchor', v)}
                placeholder="e.g., shelf layout, poster positions"
                hint="For multi-shot consistency"
              />
            </PromptSection>
          </motion.div>

          {/* Cinematography Section */}
          <motion.div
            custom={3}
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
          >
            <PromptSection
              title="Cinematography"
              iconName="storyboard"
              enabled={sections.cinematography.enabled}
              collapsed={sections.cinematography.collapsed}
              onToggleEnabled={() => updateSection('cinematography', { enabled: !sections.cinematography.enabled })}
              onToggleCollapsed={() => updateSection('cinematography', { collapsed: !sections.cinematography.collapsed })}
            >
              <PromptFieldRow>
                <PromptField
                  label="Camera Angle"
                  type="select"
                  value={values.cinematography.cameraAngle}
                  onChange={(v) => updateValue('cinematography', 'cameraAngle', v)}
                  options={CAMERA_ANGLES}
                />
                <PromptField
                  label="Focal Length"
                  type="select"
                  value={values.cinematography.focalLength}
                  onChange={(v) => updateValue('cinematography', 'focalLength', v)}
                  options={FOCAL_LENGTHS}
                />
              </PromptFieldRow>
              
              <PromptFieldRow>
                <PromptField
                  label="Depth of Field"
                  type="select"
                  value={values.cinematography.depthOfField}
                  onChange={(v) => updateValue('cinematography', 'depthOfField', v)}
                  options={DEPTH_OF_FIELD}
                />
                <PromptField
                  label="Camera Movement"
                  type="select"
                  value={values.cinematography.movement}
                  onChange={(v) => updateValue('cinematography', 'movement', v)}
                  options={CAMERA_MOVEMENTS}
                />
              </PromptFieldRow>
              
              <PromptField
                label="Camera Position Notes"
                type="text"
                value={values.cinematography.cameraPosition}
                onChange={(v) => updateValue('cinematography', 'cameraPosition', v)}
                placeholder="e.g., slightly below eye level, from doorway"
              />
            </PromptSection>
          </motion.div>

          {/* Action / Moment Section */}
          <motion.div
            custom={4}
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
          >
            <PromptSection
              title="Action / Moment"
              iconName="events"
              enabled={sections.action.enabled}
              collapsed={sections.action.collapsed}
              onToggleEnabled={() => updateSection('action', { enabled: !sections.action.enabled })}
              onToggleCollapsed={() => updateSection('action', { collapsed: !sections.action.collapsed })}
            >
              <PromptFieldRow>
                <PromptField
                  label="Pose"
                  type="text"
                  value={values.action.pose}
                  onChange={(v) => updateValue('action', 'pose', v)}
                  placeholder="e.g., leaning against counter, arms crossed"
                />
                <PromptField
                  label="Expression"
                  type="text"
                  value={values.action.expression}
                  onChange={(v) => updateValue('action', 'expression', v)}
                  placeholder="e.g., curious, confused, delighted"
                />
              </PromptFieldRow>
              
              <PromptField
                label="Dynamic Elements"
                type="text"
                value={values.action.dynamicElements}
                onChange={(v) => updateValue('action', 'dynamicElements', v)}
                placeholder="e.g., motion blur on hand, dust particles in light"
              />
              
              <PromptField
                label="Interaction"
                type="text"
                value={values.action.interaction}
                onChange={(v) => updateValue('action', 'interaction', v)}
                placeholder="e.g., reaching for object, interacting with prop"
              />
            </PromptSection>
          </motion.div>
        </div>

        {/* Preview Column */}
        <div className="prompts-view__preview">
          <motion.div
            custom={5}
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
          >
            <PromptPreview
              prompt={generatedPrompt}
              negativePrompt={negativePrompt}
            />
          </motion.div>
          
          {/* Preset Info Card */}
          {sections.quality.enabled && (
            <motion.div
              className="prompts-view__preset-info"
              custom={6}
              variants={sectionVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="prompts-view__preset-info-header">
                <span><Icon name="pin" size={14} /></span>
                <span>Current Preset</span>
              </div>
              <div className="prompts-view__preset-info-content">
                <strong>{QUALITY_PRESETS.find(p => p.id === values.quality.preset)?.name}</strong>
                <p>{QUALITY_PRESETS.find(p => p.id === values.quality.preset)?.description}</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

